import { PriorityQueue } from './pq'
import type { AStarIndex } from './aStarIndex'
import { aStarIndexOf } from './aStarIndex'
import type { Edge, Graph, Vertex } from '@metro/graph'
import type { AlgorithmConfig } from './config'

export class RoutingError extends Error {
  readonly code: 'NoPath' | 'SameOriginDestination'
  constructor(code: 'NoPath' | 'SameOriginDestination') {
    super(code)
    this.code = code
    this.name = 'RoutingError'
  }
}

/**
 * A* shortest path on a Graph.
 * Throws RoutingError('SameOriginDestination') if source and target are the
 * same vertex (same id + lineID).
 * Throws RoutingError('NoPath') if no path exists.
 *
 * Runs on the graph's vertex and edge indices (`AStarIndex`): the same
 * search, relaxation order and tie-breaks as the keyed version Android runs,
 * without building a string per relaxation.
 */
export function aStar(
  graph: Graph,
  source: Vertex,
  target: Vertex,
  config: AlgorithmConfig,
): Edge[] {
  if (source.id === target.id && source.lineID === target.lineID) {
    throw new RoutingError('SameOriginDestination')
  }
  const src = graph.vertexIndex(source)
  const dst = graph.vertexIndex(target)
  if (src === undefined || dst === undefined) throw new RoutingError('NoPath')
  const path = search(graph, aStarIndexOf(graph), src, source, dst, target, config)
  if (path === null) throw new RoutingError('NoPath')
  return path.map((edge) => graph.edgeAt(edge))
}

/** Edge indices of the cheapest path `src` → `dst`, or `null` when none. */
function search(
  graph: Graph,
  index: AStarIndex,
  src: number,
  source: Vertex,
  dst: number,
  target: Vertex,
  config: AlgorithmConfig,
): number[] | null {
  const n = graph.vertexCount
  const open = new PriorityQueue(index.vertexRank)
  const gScore = new Float64Array(n).fill(Infinity)
  const cameFrom = new Int32Array(n).fill(-1)
  const closed = new Uint8Array(n)
  const tx = Math.fround(target.x)
  const ty = Math.fround(target.y)
  const speed = config.avgRideSpeedPxPerSec

  gScore[src] = 0
  open.pushOrUpdate(src, heuristic(Math.fround(source.x), Math.fround(source.y), tx, ty, speed))

  while (!open.isEmpty) {
    const cur = open.pop()!.key
    if (cur === dst) return reconstructPath(graph, cameFrom, src, dst)
    closed[cur] = 1
    for (const edge of graph.outgoingEdges(cur)) {
      if (!graph.isActive(edge)) continue
      const next = graph.targetIndex(edge)
      if (closed[next] === 1) continue
      const tentative = gScore[cur] + index.edgeWeight[edge]
      const existing = gScore[next]
      if (tentative > existing) continue
      // An equal cost keeps the predecessor with the lower edge key, not the
      // one whose edge happened to be listed first — edge order differs per
      // platform. iOS / Android A* use the same rule.
      if (
        tentative === existing &&
        cameFrom[next] >= 0 &&
        !(index.edgeTieRank[edge] < index.edgeTieRank[cameFrom[next]])
      ) {
        continue
      }
      gScore[next] = tentative
      cameFrom[next] = edge
      open.pushOrUpdate(
        next,
        tentative + heuristic(index.edgeToX[edge], index.edgeToY[edge], tx, ty, speed),
      )
    }
  }
  return null
}

/**
 * Coordinates as the float32 iOS / Android hold them: the difference of two
 * float32 values is exact in a double, so all three compute the same bits.
 */
function heuristic(x: number, y: number, tx: number, ty: number, speed: number): number {
  const dx = x - tx
  const dy = y - ty
  return Math.sqrt(dx * dx + dy * dy) / speed
}

function reconstructPath(graph: Graph, cameFrom: Int32Array, src: number, dst: number): number[] {
  const path: number[] = []
  let cur = dst
  while (cur !== src) {
    const edge = cameFrom[cur]
    if (edge < 0) break
    path.push(edge)
    cur = graph.vertexIndex(graph.edgeAt(edge).from) ?? src
  }
  return path.reverse()
}
